import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProfiles } from "@/hooks/useProfiles";
import api from "@/api/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, Check, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { createPageUrl } from "@/utils";
import { useGlobal as useGlobalContext } from "@/components/contexts/GlobalContext";

export function ProfileSelector({ className }) {
  const navigate = useNavigate();
  const {
    profiles,
    selectedProfileId,
    selectedProfile,
    selectProfile,
    isLoading,
    isFetching,
  } = useProfiles();
  const { setCurrentProfile } = useGlobalContext();
  const [open, setOpen] = useState(false);
  const [limitStatus, setLimitStatus] = useState(null);

  useEffect(() => {
    api.profiles
      .limitStatus()
      .then((res) => setLimitStatus(res.data))
      .catch(() => {});
  }, [profiles.length]);

  const handleCreateClick = async () => {
    try {
      try {
        const onboardingRes = await api.onboarding.getStatus();
        const d = onboardingRes.data;
        const onboardingComplete =
          d?.completed === true ||
          d?.status === "completed" ||
          d?.onboarding_complete === true ||
          d?.is_complete === true;

        if (!onboardingComplete && profiles.length === 0) {
          navigate(createPageUrl("onboarding/about-you"));
          return;
        }
      } catch {
        /* proceed to limit check */
      }

      const res = await api.profiles.limitStatus();
      if (res.data?.atLimit) {
        navigate(createPageUrl("Billing"));
        return;
      }
      navigate(`${createPageUrl("onboarding/create-workspace")}?mode=new-profile`);
    } catch {
      navigate(`${createPageUrl("onboarding/create-workspace")}?mode=new-profile`);
    }
  };

  function getDotColor(profile) {
    if (!profile?.id) return "var(--ad-text-muted)";
    if (profile.profile_color) return profile.profile_color;
    const n = parseInt(profile.id.replace(/-/g, "").slice(0, 8), 16) || 0;
    const hue = n % 360;
    return `hsl(${hue}, 60%, 50%)`;
  }

  function getSubtitle(profile) {
    const parts = [profile.client_name, profile.industry].filter(Boolean);
    return parts.join(" · ");
  }

  function handleSelect(id) {
    const profile = profiles.find((p) => p.id === id);
    if (!profile) return;
    // Batch 7: stay on the current page — re-scope via selectProfile +
    // setCurrentProfile (permissions + data reload). Do not bounce to Dashboard.
    selectProfile(id);
    setCurrentProfile(profile);
    setOpen(false);
  }

  const atLimit = !!limitStatus?.atLimit;

  if (isLoading || isFetching) {
    return (
      <div className={cn("bg-white border border-ad-border rounded-lg px-4 py-2.5 min-w-[240px] animate-pulse", className)}>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded" />
          <div className="w-32 h-4 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <button
        type="button"
        onClick={atLimit ? () => navigate(createPageUrl("Billing")) : handleCreateClick}
        className={cn("bg-white border border-ad-border rounded-lg px-4 py-2.5 min-w-[240px] flex items-center gap-2 text-sm transition-colors",
          atLimit
            ? "opacity-50 cursor-not-allowed text-ad-muted"
            : "text-ad-label hover:text-white hover:border-ad-primary/30"
        )}
      >
        <Plus className="w-4 h-4 text-indigo-400" />
        <span>
          {atLimit ? "Upgrade to add more profiles" : "Create your first profile"}
        </span>
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2.5 px-3 py-2 rounded-lg",
            "hover:bg-gray-50 transition-colors min-w-0 max-w-[300px]",
            className
          )}
        >
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: getDotColor(selectedProfile) }}
          />
          <div className="flex flex-col items-start min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate leading-tight">
              {selectedProfile?.name ?? "Select Profile"}
            </span>
            {selectedProfile && (
              <span className="text-[11px] text-gray-500 truncate leading-tight">
                {getSubtitle(selectedProfile)}
              </span>
            )}
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-150",
              open && "rotate-180"
            )}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-0 bg-white border border-ad-border rounded-xl shadow-lg overflow-hidden"
      >
        <div className="py-1.5 max-h-[300px] overflow-y-auto">
          {profiles.map((profile) => {
            const isActive = profile.id === selectedProfileId;
            return (
              <button
                key={profile.id}
                type="button"
                onClick={() => handleSelect(profile.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5",
                  "text-left transition-colors",
                  isActive
                    ? "bg-[var(--ad-status-blue-bg,#f0f7ff)]"
                    : "hover:bg-gray-50"
                )}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getDotColor(profile) }}
                />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-semibold text-gray-900 truncate leading-tight">
                    {profile.name}
                  </span>
                  <span className="text-[11px] text-gray-500 truncate leading-tight">
                    {getSubtitle(profile)}
                  </span>
                </div>
                {isActive && (
                  <Check className="w-4 h-4 text-ad-primary flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        <div className="border-t border-ad-border" />

        <div className="py-1.5">
          {limitStatus === null ? null : limitStatus.atLimit ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(createPageUrl("Billing") + "?tab=overview");
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Upgrade to add more profiles
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                handleCreateClick();
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-ad-primary hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add New Profile
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
